import RemoveLayerConfirmationModal from 'samples/plate-maps/display-layer-controls/RemoveLayerConfirmationModal';
import {PlateDataParameter, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {anyWellsHaveChangedTagForParameter} from 'samples/plate-maps/PlateMapUtil';
import runModal from 'ui-library/runModal';

/**
 * Wrapper around a function to delete a parameter from a plate map. Launches a confirmation modal if there
 * are any non-default tags set for the parameter in the plate map already.
 * @param plateMap The current state of the plate map
 * @param initialPlateMap The plate of the plate map before the user started editing it
 * @param parameter The data parameter maybe being deleted
 * @param onConfirmDelete A function to be called when the user confirms they want to delete the parameter, or
 *                        immediately if no confirmation modal is needed.
 * @param onLaunchModal A function to be called immediately before the confirmation modal launches.
 */
export function confirmDeleteParameter({
  plateMap,
  initialPlateMap,
  parameter,
  onConfirmDelete,
  onLaunchModal,
}: {
  plateMap: PlateMap;
  initialPlateMap: PlateMap;
  parameter: PlateDataParameter;
  onConfirmDelete: (parameterVersionId: string) => void;
  onLaunchModal?: () => void;
}): void {
  if (anyWellsHaveChangedTagForParameter(plateMap, initialPlateMap, parameter)) {
    onLaunchModal?.();
    runModal(RemoveLayerConfirmationModal, {
      layerNameText: 'parameter',
      onRequestSubmit: () => {
        onConfirmDelete(parameter.versionId);
      },
    });
  } else {
    onConfirmDelete(parameter.versionId);
  }
}

/**
 * Adds or removes one parameter at a time from a plate map.
 * @param newSelectedIds What parameters should be selected after this function completes
 * @param oldSelectedIds What parameters were selected before this function was invoked
 * @param plateMap The plate map to add the parameter to or remove the parameter from
 * @param initialPlateMap The plate of the plate map before the user started editing it
 * @param plateDataParameters The list of data parameters available to the plate map
 * @param onLaunchConfirmDeleteModal
 */
export function toggleParameter({
  newSelectedIds,
  oldSelectedIds,
  plateMap,
  initialPlateMap,
  plateDataParameters,
  onConfirmAdd,
  onConfirmDelete,
  onLaunchConfirmDeleteModal,
}: {
  newSelectedIds: Array<string>;
  oldSelectedIds: Set<string>;
  plateMap: PlateMap;
  initialPlateMap: PlateMap;
  plateDataParameters: Array<PlateDataParameter>;
  onConfirmAdd: (parameterVersionId: string) => void;
  onConfirmDelete: (parameterVersionId: string) => void;
  onLaunchConfirmDeleteModal: () => void;
}): void {
  // Version ids are selected or deselected one id at a time. Therefore, we're only trying to delete a data
  // parameter if versionIds is smaller than selectedPlateDataParameterIds.
  if (newSelectedIds.length < oldSelectedIds.size) {
    // Which version id is getting removed?
    const newIdSet = new Set(newSelectedIds);
    const [removedVersionId] = [...oldSelectedIds].filter((id) => !newIdSet.has(id));
    const removedParameter = plateDataParameters.find(
      (parameter) => parameter.versionId === removedVersionId
    )!;
    confirmDeleteParameter({
      plateMap,
      initialPlateMap,
      parameter: removedParameter,
      onConfirmDelete,
      onLaunchModal: onLaunchConfirmDeleteModal,
    });
  } else {
    const [newId] = newSelectedIds.filter((id) => !oldSelectedIds.has(id));
    onConfirmAdd(newId);
  }
}

export function removeParameter({
  idToRemove,
  plateMap,
  initialPlateMap,
  plateDataParameters,
  onConfirmDelete,
}: {
  idToRemove: string;
  plateMap: PlateMap;
  initialPlateMap: PlateMap;
  plateDataParameters: Array<PlateDataParameter>;
  onConfirmDelete: (parameterVersionId: string) => void;
}): void {
  const removedParameter = plateDataParameters.find((parameter) => parameter.versionId === idToRemove)!;
  confirmDeleteParameter({
    plateMap,
    initialPlateMap,
    parameter: removedParameter,
    onConfirmDelete,
  });
}
